import axios from 'axios';
import { toast } from 'sonner';
import {
  getPendingHostListingInviteId,
  getPendingInviteId,
  getPendingVenueReferralData,
  getPendingStaffInviteToken,
} from './deepLinkHandler';
import {
  getLastActiveRole,
  getLastActiveRolePath,
  getLandingPathForRememberedRole,
  getBackendTargetRoleForRememberedRole,
} from './lastActiveRole';
import { identifyAirbridgeUser, trackSignin } from './airbridge';
import { persistAuthSession } from './authStorage';
import { consumePendingPushTapRoute } from './fcmPush';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Persists the auth result locally and routes the user to the correct
 * landing screen based on role + remembered shell + pending deep links.
 * Used by both the normal login flow and the 2FA verification flow so
 * they share the exact same post-login navigation logic.
 */
export async function completeLogin({ accessToken, user, onLogin, navigate }) {
  // Persist to BOTH localStorage AND Capacitor Preferences so the session
  // survives Android APK updates (the WebView-only localStorage does not).
  await persistAuthSession(accessToken, user);
  onLogin(user);

  // Airbridge: identify the user and record the signin event. `onLogin`
  // already mirrors the Airbridge user via App.js, but we set it explicitly
  // here too in case the SDK hasn't picked up the React state change yet.
  identifyAirbridgeUser(user);
  trackSignin({ user, method: 'email' });

  let role = user.role;
  const pendingInviteId = getPendingInviteId();
  const pendingHostListingInviteId = getPendingHostListingInviteId();
  const pendingVenueReferral = getPendingVenueReferralData();
  const pendingStaffInviteToken = getPendingStaffInviteToken();

  const lastRole = getLastActiveRole();
  const lastPath = getLastActiveRolePath();
  const hasDeepLink = pendingStaffInviteToken || pendingInviteId || pendingHostListingInviteId || pendingVenueReferral;

  // Buffered FCM push-tap route (e.g. tipping deep link) takes priority
  // over the remembered role shell AND the default home — the user tapped
  // the notification specifically to open that screen. Pending INVITE
  // deep links still win over it (same precedence as native App Links).
  if (!hasDeepLink) {
    const pendingPushRoute = consumePendingPushTapRoute();
    if (pendingPushRoute) {
      toast.success('Welcome back!');
      navigate(pendingPushRoute, { replace: true });
      return;
    }
  }

  if (!hasDeepLink && lastRole) {
    const remembered = getLandingPathForRememberedRole(lastRole, user, lastPath);
    if (remembered) {
      const targetBackendRole = getBackendTargetRoleForRememberedRole(lastRole);
      const needsBackendSwitch = targetBackendRole
        && (
          (targetBackendRole === 'venue_member' && role !== 'staff')
          || (targetBackendRole !== 'venue_member' && targetBackendRole !== role)
        );
      if (needsBackendSwitch) {
        try {
          const switchRes = await axios.post(
            `${API}/auth/switch-role`,
            { target_role: targetBackendRole },
            { headers: { Authorization: `Bearer ${accessToken}` } },
          );
          await persistAuthSession(switchRes.data.access_token, switchRes.data.user);
          onLogin(switchRes.data.user);
          role = switchRes.data.user.role;
        } catch {
          // Role no longer accessible (e.g. host application removed).
          // Fall through to the normal landing logic below.
        }
      }
      toast.success('Welcome back!');
      navigate(remembered, { replace: true });
      return;
    }
  }

  if (pendingStaffInviteToken) {
    toast.success('Welcome back!');
    navigate(`/staff-invite/${pendingStaffInviteToken}`, { replace: true });
  } else if (pendingInviteId) {
    // No role gate: the invite entry page auto-switches any active role back
    // to guest on mount, so hosts/venue owners logging in from an invite
    // link land on the invite instead of their role home.
    toast.success('Welcome back!');
    navigate(`/invite/${pendingInviteId}`, { replace: true });
  } else if (pendingHostListingInviteId) {
    // Host→host invites identify the recipient as a host candidate. Skip
    // the redundant "Host or Venue?" picker and land them directly on the
    // host onboarding intro (which then leads to the verify-listing page
    // with the inviter's Airbnb URL pre-filled). NO role gate — accounts
    // hold multiple roles; gating on 'guest' silently dropped invites for
    // users whose active role was host/venue/staff/admin (same class of
    // bug as the guest-invite gate removed in iter 264).
    toast.success('Welcome back!');
    navigate('/host-intro-1', { replace: true });
  } else if (pendingVenueReferral && !['admin', 'staff'].includes(role)) {
    toast.success('Welcome back!');
    navigate('/venue/intro', { replace: true });
  } else if (role === 'guest') {
    // Guests always land on the guest home — never auto-resume a venue
    // draft. If the user wants to continue a venue application, they
    // can use Switch Role → Venue Owner from their profile menu.
    toast.success('Welcome back!');
    navigate('/guest/home', { replace: true });
  } else if (role === 'staff') {
    toast.success('Welcome back!');
    try {
      const roleRes = await axios.get(`${API}/venue/staff/my-role`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const staffRole = roleRes.data?.role;
      navigate(staffRole === 'admin' ? '/staff-admin/scan' : '/staff-app/scan', { replace: true });
    } catch {
      navigate('/staff-app/scan', { replace: true });
    }
  } else if (role === 'host') {
    toast.success('Welcome back!');
    navigate('/host/active-qr', { replace: true });
  } else {
    toast.success('Welcome back!');
    navigate('/admin/dashboard', { replace: true });
  }
}
